<script setup lang="ts">
import { ref, computed } from 'vue';
import { useDashboardStore } from '@Stores/dashboard.store';
import PropertySheet from '@Components/PropertySheet.vue';
import { isPropertyOwnerRole } from '@Src/shared/utils/participantRole';

const dashboardStore = useDashboardStore();

const isSheetOpen = ref(false);

const hasMultipleDeals = computed(() => dashboardStore.deals.length > 1);

// DS-2743: only Buyer/Seller actually own the property — for anyone else on
// the deal (agent, attorney, lender, title company, etc.) the house icon is
// visual noise. Gate on the active deal's participantRole.
const showHouseIcon = computed(() => isPropertyOwnerRole(dashboardStore.activeDeal?.participantRole));

const currentLabel = computed(() => {
    const deal = dashboardStore.activeDeal;
    if (!deal) return 'Select property';
    const full = deal.propertyAddress;
    if (!full) return 'Closing';
    return full.split(',')[0].trim();
});

const triggerAriaLabel = computed(() => {
    const deal = dashboardStore.activeDeal;
    if (!deal) return 'Select property';
    const full = deal.propertyAddress;
    if (!full) return 'Select closing';
    return `Select property — ${full.split(',')[0].trim()}`;
});

const handleTriggerClick = () => {
    if (hasMultipleDeals.value) isSheetOpen.value = true;
};
</script>

<template>
    <button
        class="property-trigger"
        :class="{ 'property-trigger--static': !hasMultipleDeals }"
        type="button"
        :aria-label="triggerAriaLabel"
        :aria-haspopup="hasMultipleDeals ? 'dialog' : undefined"
        :aria-expanded="hasMultipleDeals ? isSheetOpen : undefined"
        @click="handleTriggerClick"
    >
        <app-icon v-if="showHouseIcon" icon="cd-ic-home" class="property-trigger__icon" />
        <span class="property-trigger__label">{{ currentLabel }}</span>
        <app-icon v-if="hasMultipleDeals" icon="cd-ic-caret-down" class="property-trigger__chevron" />
    </button>

    <PropertySheet v-if="hasMultipleDeals" v-model:open="isSheetOpen" />
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.property-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid $portal-border;
    border-radius: 9999px;
    background: $portal-surface;
    color: $portal-text;
    // Buttons don't inherit the page font-family by default.
    font-family: inherit;
    font-size: $text-portal-caption;
    font-weight: 500;
    cursor: pointer;
    transition: background-color $portal-duration-fast $portal-ease-standard;

    &:hover {
        background: $portal-bg;
    }

    // Single-deal state: pill is informational only — no sheet, no caret,
    // no hover affordance, default cursor.
    &--static {
        cursor: default;

        &:hover {
            background: $portal-surface;
        }
    }

    &__icon,
    &__chevron {
        font-size: 14px;
        color: $portal-text-mid;
        flex-shrink: 0;
    }

    &__label {
        // Show the street on every breakpoint — mobile previously hid it,
        // which left the trigger as an unlabeled icon pill (DS-2869).
        // Tighter mobile max-width so the label can't push the nav past
        // a narrow viewport when combined with the logo wrapper.
        // inline-block (not plain inline) so text-overflow: ellipsis below
        // is well-defined per spec — pre-existing pattern, now spec-correct.
        display: inline-block;
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: left;

        @media (min-width: 768px) {
            max-width: 200px;
        }
    }
}
</style>
